import React from "react";
import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";

// Radial tint that lerps the scene toward the active project's color —
// our version of Bodak's evolving island.
export const SceneTint: React.FC<{
  color: string;
  enterFrame?: number;
  intensity?: number;
  center?: string;
}> = ({ color, enterFrame = 0, intensity = 1, center = "50% 40%" }) => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill
      style={{
        backgroundImage: `radial-gradient(ellipse at ${center}, ${color}55 0%, ${color}18 45%, transparent 75%)`,
        opacity:
          intensity *
          interpolate(frame, [enterFrame, enterFrame + 18], [0, 1], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          }),
        pointerEvents: "none",
      }}
    />
  );
};

// Era header shown at the top of a scene.
export const SceneHeading: React.FC<{
  title: string;
  subtitle?: string;
  color: string;
  enterFrame?: number;
}> = ({ title, subtitle, color, enterFrame = 0 }) => {
  const frame = useCurrentFrame();
  return (
    <div
      style={{
        position: "absolute",
        top: 60,
        left: 0,
        right: 0,
        textAlign: "center",
        opacity: interpolate(frame, [enterFrame, enterFrame + 12], [0, 1], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        }),
        translate: `0px ${interpolate(
          frame,
          [enterFrame, enterFrame + 12],
          [-30, 0],
          { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
        )}px`,
      }}
    >
      <div
        style={{
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 600,
          fontSize: 48,
          color: "white",
        }}
      >
        {title}
      </div>
      {subtitle ? (
        <div
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 26,
            color,
            marginTop: 8,
          }}
        >
          {subtitle}
        </div>
      ) : null}
    </div>
  );
};

// Frame-driven terminal used by the boot and contact scenes.
// Types `lines` sequentially starting at their `from` frame.
export type TerminalLine = {
  text: string;
  from: number;
  charsPerFrame?: number;
  color?: string;
};

export const Terminal: React.FC<{
  lines: TerminalLine[];
  width?: number;
  fontSize?: number;
}> = ({ lines, width = 900, fontSize = 30 }) => {
  const frame = useCurrentFrame();
  return (
    <div
      style={{
        width,
        borderRadius: 16,
        border: "2px solid #333",
        backgroundColor: "rgba(12, 12, 12, 0.95)",
        boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
        overflow: "hidden",
        textAlign: "left",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 10,
          padding: "14px 18px",
          backgroundColor: "#1c1c1c",
        }}
      >
        {["#ff5f57", "#febc2e", "#28c840"].map((dotColor) => (
          <div
            key={dotColor}
            style={{
              width: 16,
              height: 16,
              borderRadius: 8,
              backgroundColor: dotColor,
            }}
          />
        ))}
      </div>
      <div style={{ padding: "28px 34px", minHeight: fontSize * 1.6 * lines.length }}>
        {lines.map((line, lineIndex) => {
          const isLast = lineIndex === lines.length - 1;
          const visibleChars = Math.max(
            0,
            Math.floor((frame - line.from) * (line.charsPerFrame ?? 1.5))
          );
          const text = line.text.slice(0, visibleChars);
          const isTyping =
            visibleChars > 0 && visibleChars < line.text.length;
          const done = visibleChars >= line.text.length;
          return (
            <div
              key={`${line.from}-${line.text}`}
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize,
                lineHeight: 1.6,
                color: line.color ?? "white",
                whiteSpace: "pre-wrap",
                minHeight: fontSize * 1.6,
              }}
            >
              {visibleChars > 0 ? `>> ${text}` : ""}
              {isTyping || (done && isLast && Math.floor(frame / 8) % 2 === 0) ? (
                <span style={{ color: "#fcaeea" }}>▍</span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};
